import React from 'react';
import { View, StyleSheet, ScrollView, useWindowDimensions, Platform } from 'react-native';
import { Button, Surface, Text } from 'react-native-paper';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import LoadingImage from '../components/LoadingImage';
import LessonGeneratorEmbed from '../components/LessonGeneratorEmbed';
import HeaderBar from '../components/HeaderBar';
import { getLessonGeneratorUrl } from '../config/lessonGenerator';
import { brandButton, landingAccent, landingLink, landingLabel } from '../theme/paperTheme';

type RootStackParamList = {
  Landing: undefined;
  Login: undefined;
  Register: undefined;
  Home: undefined;
};

type LandingScreenProps = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Landing'>;
};

const HEADER_CHROME_MOBILE = 280;
const HEADER_CHROME_DESKTOP = 100;

const LandingScreen: React.FC<LandingScreenProps> = ({ navigation }) => {
  const { width, height: windowHeight } = useWindowDimensions();
  const isMobile = width < 900;
  const isDesktop = width >= 900;
  const lessonGeneratorUrl = getLessonGeneratorUrl();
  const generatorHeight = Math.max(
    Math.round(windowHeight - (isMobile ? HEADER_CHROME_MOBILE : HEADER_CHROME_DESKTOP)),
    320,
  );

  const headerRightContent = (
    <View style={styles.headerButtons}>
      <Button mode="text" onPress={() => navigation.navigate('Login')} textColor={landingLink}>
        Login
      </Button>
      <Button
        mode="contained"
        onPress={() => navigation.navigate('Register')}
        style={styles.headerRegisterButton}
        buttonColor={brandButton}
        textColor="#ffffff"
      >
        Register
      </Button>
    </View>
  );

  const renderHeader = () => (
    <View style={styles.headerWrap}>
      <HeaderBar
        style={[styles.headerBar, isDesktop && styles.headerBarDesktop]}
        rightContent={headerRightContent}
      />
    </View>
  );

  const renderGenerator = (mobileLayout: boolean) => {
    const embed = lessonGeneratorUrl ? (
      <LessonGeneratorEmbed
        uri={lessonGeneratorUrl}
        containerStyle={[
          styles.generatorEmbed,
          mobileLayout ? styles.generatorEmbedMobile : styles.generatorEmbedDesktop,
        ]}
      />
    ) : (
      <LoadingImage
        source={require('../../assets/login-short.png')}
        style={styles.fallbackImage}
        contentFit={mobileLayout ? 'contain' : 'cover'}
        transition={300}
        cachePolicy="memory-disk"
      />
    );

    if (mobileLayout) {
      return (
        <View style={[styles.generatorContainer, styles.generatorContainerMobile, { height: generatorHeight }]}>
          {embed}
        </View>
      );
    }

    return <View style={styles.desktopEmbedContainer}>{embed}</View>;
  };

  const renderCtaCard = () => (
    <Surface style={styles.ctaCard} elevation={0}>
      <View style={styles.ctaLabelRow}>
        <View style={styles.ctaAccentDot} />
        <Text style={styles.ctaLabel}>Teachers Lobby</Text>
      </View>
      <Text style={styles.ctaBody}>
        Want access to teacher support and other resources? Join the community.
      </Text>
      <Button
        mode="contained"
        onPress={() => navigation.navigate('Register')}
        style={styles.ctaButton}
        buttonColor={brandButton}
        textColor="#ffffff"
      >
        Create free account
      </Button>
      <Button
        mode="text"
        onPress={() => navigation.navigate('Login')}
        style={styles.ctaLink}
        textColor={landingLink}
      >
        Already have an account? Log in
      </Button>
    </Surface>
  );

  if (isDesktop) {
    return (
      <View style={styles.container}>
        <Surface style={[styles.surface, styles.surfaceDesktop, { height: windowHeight }]}>
          {renderHeader()}
          {renderGenerator(false)}
        </Surface>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        <Surface style={[styles.surface, styles.surfaceMobile]}>
          {renderHeader()}
          <View style={styles.content}>
            {renderGenerator(true)}
            {renderCtaCard()}
          </View>
        </Surface>
      </ScrollView>
    </View>
  );
};

const cardShadow = Platform.OS === 'web'
  ? { boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)' }
  : {
      shadowColor: '#0F172A',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.06,
      shadowRadius: 12,
      elevation: 3,
    };

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F4F7FB',
  },
  scrollContainer: {
    flexGrow: 1,
    backgroundColor: '#F4F7FB',
  },
  surface: {
    flex: 1,
    elevation: 0,
    backgroundColor: '#F4F7FB',
  },
  surfaceMobile: {
    paddingBottom: 24,
  },
  surfaceDesktop: {
    overflow: 'hidden',
  },
  headerWrap: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)' }
      : {
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(15, 23, 42, 0.06)',
        }),
  },
  headerBar: {
    marginHorizontal: 8,
    marginBottom: 0,
  },
  headerBarDesktop: {
    marginHorizontal: 48,
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  headerRegisterButton: {
    borderRadius: 999,
  },
  content: {
    flex: 1,
    width: '100%',
    maxWidth: 1280,
    alignSelf: 'center',
  },
  desktopEmbedContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
  },
  generatorContainer: {
    width: '100%',
    overflow: 'hidden',
    alignSelf: 'center',
    backgroundColor: '#FFFFFF',
  },
  generatorContainerMobile: {
    borderRadius: 0,
  },
  generatorEmbed: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  generatorEmbedMobile: {
    borderRadius: 0,
  },
  generatorEmbedDesktop: {
    borderRadius: 0,
  },
  fallbackImage: {
    width: '100%',
    height: '100%',
  },
  ctaCard: {
    marginHorizontal: 20,
    marginTop: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...cardShadow,
  },
  ctaLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 8,
  },
  ctaAccentDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: landingAccent,
  },
  ctaLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: landingLabel,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  ctaBody: {
    fontSize: 15,
    color: '#64748B',
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 16,
  },
  ctaButton: {
    borderRadius: 999,
    paddingVertical: 2,
  },
  ctaLink: {
    marginTop: 4,
  },
});

export default LandingScreen;
